import type { PartialMessages } from './tr';
import { adminEn } from './admin.en';
import { mobileEn } from './mobile.en';

import { commonEn, errorsEn } from './shared.en';
import { faz8PermissionsEn } from './faz8.en';
import { storefrontEn } from './storefront.en';
import { aiEn } from './ai.en';

export const en = {
  admin: adminEn,
  mobile: mobileEn,
  ai: aiEn.ui,
  common: commonEn,
  errors: errorsEn,
  roles: {
    owner: 'Business Owner',
    admin: 'Administrator',
    store_manager: 'Store Manager',
    catalog_manager: 'Catalog Manager',
    pricing_manager: 'Pricing Manager',
    operations: 'Orders / Operations',
    warehouse: 'Warehouse',
    finance: 'Finance / Accounting',
    marketing: 'Marketing',
    content_editor: 'Content Editor',
    vet_reviewer: 'Veterinary Expert Reviewer',
    customer_service: 'Customer Service',
    dealer_rep: 'Dealer Sales Rep',
    read_only: 'Read Only / Auditor',
  },
  roleDescriptions: {
    owner: 'Full access. At least one person must have this role.',
    admin: 'Same as owner except granting the owner role.',
    store_manager:
      'Runs daily operations: catalog, orders, stock, customers, campaigns. No payment keys or role management.',
    catalog_manager:
      'Manages products, categories, images and SEO fields. Sees prices but cannot bulk-change them.',
    pricing_manager: 'Manages price lists, bulk pricing and campaign prices; sees costs.',
    operations: 'Prepares and ships orders, handles return requests (no refund approval).',
    warehouse: 'Stock movements, counts and transfers; cannot see prices or customer contact details.',
    finance: 'Payments, refunds, reconciliation, invoices and finance reports.',
    marketing: 'Campaigns, coupons, email/SMS and ad reports.',
    content_editor: 'Writes page and article drafts; cannot publish.',
    vet_reviewer:
      'Approves health/supplement content and product health claims. Health content cannot be published without this approval.',
    customer_service: 'Sees customers and orders, answers support tickets.',
    dealer_rep: 'Manages orders and quotes of assigned dealers.',
    read_only: 'Sees everything, changes nothing; personal data is masked.',
  },
  permissions: {
    ...aiEn.permissions,
    ...faz8PermissionsEn,
    'org.settings.read': 'View business settings',
    'org.settings.update': 'Change business details and settings',
    'org.brand.read': 'View brands',
    'org.brand.manage': 'Create and edit brands',
    'org.store.read': 'View stores',
    'org.store.manage': 'Manage stores, domains and markets',
    'org.store.go_live': 'Take stores live, into maintenance or offline (sensitive)',
    'org.data.migrate': 'Export/import the migration package (owner only; sensitive)',
    'org.user.read': 'View users',
    'org.user.manage': 'Invite users, change roles, suspend',
    'org.role.manage': 'Create and edit custom roles',
    'org.api_key.manage': 'Create and revoke API keys',
    'org.webhook.manage': 'Manage webhooks',
    'org.integration.read': 'View integration status',
    'org.integration.manage': 'Enter and test integration credentials',
    'org.integration.go_live': 'Switch integrations to live',
    'org.audit.read': 'View audit logs',
    'org.system.read': 'View system status',
    'org.setup.manage': 'Manage the setup wizard',
    'org.help.manage': 'Edit help texts',
    'org.file.upload': 'Upload files and images',
    'org.job.read': 'View background jobs',
    'catalog.product.read': 'View products',
    'catalog.product.write': 'Create and edit products',
    'catalog.product.publish': 'Publish products',
    'catalog.health_claim.approve': 'Approve product health claims',
    'catalog.import': 'Import products',
    'catalog.bulk.apply': 'Apply and revert bulk product operations',
    'pricing.price.read': 'View prices',
    'pricing.price.write': 'Edit prices',
    'pricing.bulk.apply': 'Apply bulk price changes',
    'pricing.cost.read': 'View costs and margins',
    'inventory.stock.read': 'View stock',
    'inventory.stock.write': 'Stock movements and counts',
    'customers.customer.read': 'View customers',
    'customers.customer.write': 'Edit customers',
    'customers.pii.read': 'See unmasked customer personal data',
    'privacy.request.read': 'View data subject requests',
    'privacy.request.manage': 'Record and resolve data subject requests',
    'privacy.retention.read': 'View retention periods and the destruction log',
    'privacy.retention.manage': 'Turn on and change automatic destruction rules',
    'privacy.data.export': 'Data subject: download a copy of personal data',
    'privacy.data.erase': 'Data subject: anonymize personal data (irreversible)',
    'b2b.dealer.read': 'View dealers',
    'b2b.dealer.manage': 'Edit dealer details',
    'b2b.dealer.approve': 'Approve/reject dealer applications',
    'b2b.ledger.read': 'View dealer accounts (balance, statement)',
    'b2b.ledger.manage': 'Record dealer payments, adjustments and opening balances; set payment terms',
    'b2b.quote.read': 'View dealer quotes',
    'b2b.quote.manage': 'Prepare, send and cancel dealer quotes',
    'b2b.sales.manage': 'Manage sales regions; assign dealers to reps and regions',
    'b2b.commission.read': 'View sales rep commissions',
    'b2b.commission.manage': 'Manage commission rules, period calculation, approval and payment marking',
    'orders.order.read': 'View orders',
    'orders.order.write': 'Edit orders',
    'orders.refund.approve': 'Approve refunds',
    'payments.payment.read': 'View payments',
    'payments.payment.capture': 'Mark bank transfer, cash on delivery and open account payments as received',
    'payments.giftcard.read': 'View gift cards',
    'payments.giftcard.issue': 'Issue gift cards, adjust balances, disable cards (sensitive)',
    'customers.credit.manage': 'Add or deduct customer store credit (sensitive)',
    'payments.provider.manage': 'Payment settings, bank accounts and provider keys; take card payments live',
    'shipping.shipment.write': 'Shipping operations',
    'shipping.method.manage': 'Shipping methods and rates',
    'invoices.invoice.read': 'View invoices',
    'invoices.invoice.write': 'Create and send invoices',
    'legal.document.read': 'View legal texts',
    'legal.document.write': 'Draft legal texts',
    'legal.document.publish': 'Publish legal texts',
    'content.read': 'View content',
    'content.write': 'Write content',
    'content.publish': 'Publish content',
    'content.expert_review': 'Give expert (veterinary) approval',
    'catalog.question.read': 'View product questions',
    'catalog.question.answer': 'Answer and publish product questions',
    'catalog.review.read': 'View product reviews',
    'catalog.review.moderate': 'Approve, reject and reply to product reviews',
    'theme.read': 'View theme',
    'theme.edit': 'Edit theme',
    'theme.publish': 'Publish theme',
    'seo.read': 'View SEO reports',
    'seo.write': 'Edit SEO settings',
    'tracking.read': 'View measurement settings and cookie consent statistics',
    'tracking.manage': 'Edit storefront tags (GTM, GA4, Meta, TikTok)',
    'marketing.campaign.read': 'View campaigns',
    'marketing.audience.read': 'View subscribers, suppression list and İYS records',
    'marketing.audience.manage': 'Manage subscriptions and suppression list, mark İYS records',
    'marketing.segment.manage': 'Create customer segments and recompute RFM',
    'marketing.campaign.write': 'Prepare campaigns',
    'marketing.campaign.send': 'Send campaigns',
    'reports.sales.read': 'Sales reports',
    'reports.finance.read': 'Finance reports',
    'automation.manage': 'Manage automations',
    'marketing.loyalty.manage': 'Manage loyalty program',
    'marketing.referral.manage': 'Manage referral program',
    'marketing.affiliate.manage': 'Manage affiliates and commissions',
    'customers.ticket.read': 'View support tickets',
    'customers.ticket.manage': 'Reply to and manage support tickets',
    'customers.chat.read': 'View the live chat queue',
    'customers.chat.manage': 'Reply in live chat and manage chat settings',
    'marketing.ads.read': 'View the ad center',
    'marketing.ads.manage': 'Manage ad accounts, data uploads and change requests',
    'marketing.ads.approve': 'Approve ad budget and publishing changes',
  },
  permissionModules: {
    ...aiEn.permissionModules,
    org: 'Business & settings',
    catalog: 'Catalog',
    pricing: 'Pricing',
    inventory: 'Inventory',
    customers: 'Customers',
    b2b: 'Dealers',
    orders: 'Orders',
    payments: 'Payments',
    shipping: 'Shipping',
    invoices: 'Invoices',
    content: 'Content',
    theme: 'Theme',
    seo: 'SEO',
    marketing: 'Marketing',
    reports: 'Reports',
    automation: 'Automation',
  },
  integrationStatus: {
    NOT_CONFIGURED: 'Setup required',
    SANDBOX: 'Sandbox',
    LIVE: 'Live',
    ERROR: 'Error',
    DISABLED: 'Disabled',
  },
  integrationCategories: {
    email: 'Email',
    payment: 'Payments',
    google: 'Google',
    ads: 'Ads & measurement',
    messaging: 'Messaging & consent',
    logistics: 'Shipping',
    invoicing: 'e-Invoice',
    infrastructure: 'Infrastructure',
    ai: 'AI',
    mobile: 'Mobile',
    seo: 'SEO data',
  },
  integrations: {
    smtp: {
      name: 'SMTP email',
      description: 'Sends order, password and invitation emails from your own mail server.',
    },
    ses: {
      name: 'Amazon SES',
      description: 'Sends email through the Amazon SES API; bounces and complaints arrive via SNS.',
    },
    sendgrid: {
      name: 'SendGrid',
      description:
        'Sends email through the SendGrid API; signed event webhook for bounces, complaints, unsubscribes.',
    },
    postmark: {
      name: 'Postmark',
      description:
        'Separate Postmark streams for transactional and broadcast email; bounce and complaint webhooks.',
    },
    iyzico: { name: 'iyzico', description: 'Cards, 3D Secure, installments and refunds.' },
    paytr: { name: 'PayTR', description: 'Cards, installments, bank transfer payment page.' },
    param: {
      name: 'Param',
      description:
        "Setup required: the amount format, return fields and live address of Param's hosted payment method are not in the public documentation; ask Param for documentation.",
    },
    sipay: {
      name: 'Sipay',
      description:
        'Credit card, 3D Secure and instalments on the Sipay payment page; partial refunds. Going live requires a static IP allow-list at Sipay.',
    },
    paynet: {
      name: 'Paynet',
      description:
        'Paynet merged with iyzico (iyzico brand since 1 January 2026); use the iyzico connection.',
    },
    stripe: {
      name: 'Stripe',
      description:
        'Foreign-currency card payments and wallets (Apple Pay, Google Pay) on Stripe’s payment page. The account is opened for a company in a Stripe-supported country (Türkiye is not supported).',
    },
    paypal: {
      name: 'PayPal',
      description:
        'Pay with PayPal (EUR, USD, GBP). The account belongs to a company outside Türkiye; PayPal does not operate in Türkiye.',
    },
    rank_tracking: {
      name: 'Rank tracking',
      description:
        'Tracking daily Google rankings of keywords needs a licensed (paid) data provider (e.g. DataForSEO, Semrush). The owner chooses the provider; until then position comes from the Search Console average.',
    },
    pagespeed: {
      name: 'PageSpeed Insights',
      description:
        'Page speed (Core Web Vitals): home, category, product and blog post measured weekly. Google Cloud API key; free.',
    },
    google_oauth: {
      name: 'Google account (OAuth client)',
      description:
        'Google Cloud OAuth client for Search Console, Analytics and Merchant Center. Connect on the Google screen.',
    },
    google_merchant: {
      name: 'Google Merchant Center',
      description: 'Product feed, free listings and Shopping.',
    },
    google_search_console: {
      name: 'Google Search Console',
      description: 'Search performance, sitemaps and indexing reports.',
    },
    google_analytics: { name: 'Google Analytics 4', description: 'Traffic and sales analytics.' },
    google_tag_manager: { name: 'Google Tag Manager', description: 'Tag management.' },
    google_ads: { name: 'Google Ads', description: 'Ad reports and conversion uploads.' },
    meta_ads: {
      name: 'Meta ad spend',
      description: 'Reads Facebook and Instagram ad spend, impressions and clicks daily (read only).',
    },
    tiktok_ads: {
      name: 'TikTok ad spend',
      description: 'Reads TikTok ad spend, impressions and clicks daily (read only).',
    },
    google_business_profile: { name: 'Google Business Profile', description: 'Business info and reviews.' },
    meta: { name: 'Meta (Facebook/Instagram)', description: 'Pixel and Conversions API.' },
    tiktok: { name: 'TikTok', description: 'Pixel and Events API.' },
    iys: { name: 'İYS', description: 'Turkish commercial message consent registry.' },
    netgsm: {
      name: 'Netgsm (SMS)',
      description:
        'Campaign SMS and delivery reports. The header must be approved at Netgsm and an İYS brand code set. The test checks the balance; no SMS is sent.',
    },
    iletimerkezi: {
      name: 'İleti Merkezi (SMS)',
      description:
        'Campaign SMS and delivery reports. The header must be approved. The test checks the balance; no SMS is sent.',
    },
    verimor: {
      name: 'Verimor (SMS)',
      description:
        'Campaign SMS and delivery reports. Verimor needs an API IP allowlist and an approved header. The test checks the balance; no SMS is sent.',
    },
    whatsapp: {
      name: 'WhatsApp Business (Cloud API)',
      description:
        'Campaign messages with Meta-approved templates and delivery webhooks. The test reads the phone number details; no message is sent.',
    },
    yurtici: {
      name: 'Yurtiçi Kargo',
      description: 'Shipment registration and tracking (carrier web service).',
    },
    aras: { name: 'Aras Kargo', description: 'Shipment registration and tracking (carrier web service).' },
    mng: {
      name: 'MNG Kargo (DHL eCommerce)',
      description: 'Shipments, barcode labels and tracking (Apizone).',
    },
    geliver: {
      name: 'Geliver (multi-carrier)',
      description: 'Many carriers with one connection: rates, labels, tracking.',
    },
    google_signin: {
      name: 'Sign in with Google (customers)',
      description:
        'Continue with Google for storefront customers: a separate OAuth client; scopes openid, email, profile only. Turned on per store under Stores → Customer sign-in.',
    },
    bizimhesap: {
      name: 'BizimHesap',
      description: 'Invoice records from orders; e-Archive/e-Invoice issued in BizimHesap.',
    },
    cloudflare: {
      name: 'Cloudflare',
      description:
        "CDN, WAF and DNS. Whether to use it is the owner's decision (optional); the site works without Cloudflare.",
    },
    s3_storage: {
      name: 'Object storage (S3 / R2)',
      description:
        'Cloudflare R2, Backblaze B2, Hetzner or another S3-compatible store: images and documents off the server and an encrypted off-site copy of nightly backups.',
    },
    error_tracking: { name: 'Error tracking', description: 'Monitor application errors.' },
    observability: {
      name: 'Observability (OpenTelemetry)',
      description:
        'Request and background job durations, error rates and traces are sent over OTLP to a collector (Grafana Cloud, Honeycomb, New Relic, your own OpenTelemetry Collector). No personal data: only method, route template, status code and job name.',
    },
    firebase: {
      name: 'Firebase (Android notifications)',
      description:
        'Notifications such as new orders and return requests in the Android admin app (FCM). Enter project_id, client_email and private_key from the Firebase service account key.',
    },
    // Faz 9: ai_text, ai_image ve youtube metinleri ai.*.ts'de.
    ...aiEn.integrations,
  },
  integrationFields: {
    ...aiEn.integrationFields,
    iysCode: 'İYS number (service provider code)',
    brandCode: 'Brand code',
    autoSend: 'Send consents and refusals to İYS automatically',
    endpoint: 'Endpoint URL (S3 API, https://…)',
    region: 'Region (e.g. eu-central-1 for Amazon; auto for R2, empty = auto)',
    bucket: 'Bucket name',
    accessKeyId: 'Access Key ID',
    secretAccessKey: 'Secret Access Key',
    prefix: 'Folder prefix (optional, e.g. zootopia/)',
    storeMedia: 'Store new images and documents in this storage',
    backupCopies: 'Upload an encrypted off-site copy of backups here',
    backupKeepDays: 'Off-site backup retention (days; empty = 30)',
    dsn: 'DSN (error tracking project address)',
    otlpEndpoint: 'Collector address (OTLP/HTTP, https://…)',
    otlpHeaders: 'Auth headers (Name=value, comma-separated; e.g. Authorization=Basic …)',
    sampleRatio: 'Trace sampling ratio (0–1; empty = 1, every request)',
    host: 'Server host',
    port: 'Port',
    secure: 'SSL/TLS (enable for port 465)',
    username: 'Username',
    password: 'Password',
    fromEmail: 'From email',
    fromName: 'From name',
    apiKey: 'API key',
    secretKey: 'Secret key',
    merchantId: 'Merchant ID',
    merchantKey: 'Merchant key',
    merchantSalt: 'Merchant salt',
    wsUserName: 'Web service username',
    wsPassword: 'Web service password',
    codUserName: 'Cash on delivery username (if any)',
    codPassword: 'Cash on delivery password (if any)',
    setOrderUserName: 'Shipment creation username',
    setOrderPassword: 'Shipment creation password',
    queryUserName: 'Query username',
    queryPassword: 'Query password',
    customerCode: 'Customer code',
    clientId: 'Client ID',
    clientSecret: 'Client secret',
    webhookSecret: 'Webhook signing secret (whsec_…)',
    appId: 'Application ID (app_id)',
    saleWebhookKey: 'Sale webhook key',
    webhookId: 'Webhook ID',
    customerNumber: 'Customer number',
    apiToken: 'API token',
    firmId: 'Firm ID (firmId)',
    token: 'Access token (firm ID is used if empty)',
    configurationSetName: 'Configuration set (optional)',
    snsTopicArn: 'SNS topic ARN (for bounce/complaint notifications)',
    webhookPublicKey: 'Signed Event Webhook public key',
    euRegion: 'EU region account (api.eu.sendgrid.com)',
    serverToken: 'Server API token',
    transactionalStream: 'Transactional stream (default outbound)',
    broadcastStream: 'Broadcast stream ID (default broadcast)',
    apiHash: 'API secret (hash)',
    header: 'SMS header (approved sender name, max 11 characters)',
    accessToken: 'Access token (system user, permanent)',
    phoneNumberId: 'Phone number ID',
    wabaId: 'WhatsApp Business Account ID (WABA ID)',
    appSecret: 'Application secret (app_secret / App secret)',
    verifyToken: 'Webhook verify token (chosen by you)',
    apiVersion: 'Graph API version (optional, default v23.0)',
    projectId: 'Project ID (project_id)',
    clientEmail: 'Service account email (client_email)',
    privateKey: 'Private key (the whole private_key value from the JSON file)',
    loginCustomerId: 'Manager account ID (MCC, optional, 123-456-7890)',
    adAccountId: 'Ad account ID (with act_ or digits only)',
    advertiserId: 'Advertiser ID',
  },
  integrationMessages: {
    ...aiEn.integrationMessages,
    health_timeout: 'The provider did not respond in time.',
    health_unreachable: 'The provider could not be reached.',
    health_skipped_messages:
      'This test sends a real message or event to the provider, so it is not run automatically. Use the connection’s "Test connection" button.',
    health_skipped_remote_write:
      'This test creates and deletes a test record at the provider, so it is not run automatically. Use the connection’s "Test connection" button.',
    health_skipped_paid_quota:
      'This test uses paid quota, so it is not run automatically. Use the connection’s "Test connection" button.',
    health_skipped_unknown: 'No automatic check is defined for this connection.',
    ses_ok: 'Connected to Amazon SES; sending is enabled.',
    ses_ok_sandbox:
      'Connected to Amazon SES, but the account is in the sandbox and only sends to verified addresses. Request production access from AWS.',
    ses_invalid_settings: 'Region (e.g. eu-central-1), access keys and sender email are required.',
    ses_auth_failed: 'Amazon access keys were not accepted or lack SES permission.',
    ses_unreachable: 'Amazon SES could not be reached.',
    ses_sending_disabled: 'Sending is disabled on this Amazon SES account (it may be paused).',
    sendgrid_ok: 'Connected to SendGrid; the key can send mail.',
    sendgrid_invalid_settings: 'API key and sender email are required.',
    sendgrid_auth_failed: 'The SendGrid API key was not accepted.',
    sendgrid_missing_scope: 'The API key lacks the "Mail Send" permission.',
    sendgrid_unreachable: 'SendGrid could not be reached.',
    postmark_ok: 'Connected to Postmark.',
    postmark_invalid_settings: 'Server token and sender email are required.',
    postmark_auth_failed: 'The Postmark server token was not accepted.',
    postmark_unreachable: 'Postmark could not be reached.',
    iys_ok: 'Connected to İYS (token received); no consent was sent.',
    iys_invalid_settings: 'İYS number, brand code (digits only), username and password are required.',
    iys_auth_failed: 'İYS username or password was not accepted.',
    iys_rejected: 'İYS rejected the record.',
    iys_unreachable: 'İYS could not be reached.',
    s3_ok: 'A test file was written, read back and deleted; the connection works.',
    s3_endpoint_invalid:
      'Endpoint, bucket name or keys are missing or invalid (an https S3 API URL is required).',
    s3_auth_failed: 'The storage keys were rejected or have no write access to this bucket.',
    s3_bucket_missing: 'No bucket with this name; create it in the provider console.',
    s3_unreachable: 'The storage server could not be reached.',
    s3_verify_failed: 'The test file could not be read back unchanged.',
    pagespeed_ok: 'PageSpeed Insights key verified; a sample page was measured.',
    pagespeed_key_format: 'The API key format is wrong.',
    pagespeed_key_invalid: 'Google did not accept this API key.',
    pagespeed_quota: 'The PageSpeed Insights daily quota is used up.',
    pagespeed_unreachable: 'Could not reach PageSpeed Insights.',
    error_tracking_ok:
      'A test event was sent to the error tracking service; "Zootopia bağlantı testi" should appear in its dashboard.',
    error_tracking_invalid_dsn:
      'Invalid DSN. Paste the "Client Keys (DSN)" address from the project settings.',
    error_tracking_failed:
      'The error tracking service did not accept the test event; check the DSN and project.',
    observability_ok:
      'The collector accepted an empty trace batch. Request and job durations will appear in the collector within a few minutes.',
    observability_invalid:
      'The collector address or headers are invalid. The address must start with https (e.g. https://otlp-gateway-….grafana.net/otlp); headers as "Name=value", comma-separated.',
    observability_failed:
      "The collector did not accept the test batch; check the address and the auth header (e.g. Authorization) in the collector's OTLP settings.",
    smtp_ok: 'Connected to the SMTP server and authenticated.',
    google_client_ok: 'Google client ID and secret verified. Now connect your account on the Google screen.',
    google_client_invalid: 'Google did not accept the client ID or secret.',
    google_client_invalid_format:
      'The client ID must end with ".apps.googleusercontent.com"; the secret cannot be empty.',
    google_signin_needs_domain:
      "Setup required: the test uses the store's real redirect URI. Add a storefront domain under Stores first.",
    google_invalid_request:
      "Google rejected the request (invalid_request): {{description}} Redirect URI tried: {{redirectUri}}. It must be a real https domain and listed among the client's authorized redirect URIs in Google Cloud.",
    google_redirect_uri_mismatch:
      "The redirect URI isn't registered on the client in Google Cloud (redirect_uri_mismatch): {{redirectUri}}. Add it under Google Auth Platform → Clients → client → Authorized redirect URIs. {{description}}",
    google_client_rejected: 'Google rejected the request ({{error}}): {{description}}',
    google_unreachable: 'Google could not be reached. Try again later.',
    smtp_failed: 'SMTP connection failed.',
    smtp_invalid_settings: 'Host or port is invalid.',
    smtp_private_host_blocked: 'Internal network addresses are blocked for security.',
    iyzico_ok: 'iyzico connection verified.',
    iyzico_credentials_missing: 'iyzico API key and secret key are missing.',
    iyzico_not_sandbox_key: 'The test environment needs an iyzico test key starting with "sandbox-".',
    iyzico_sandbox_key_in_live: 'A test (sandbox) key was entered for the live environment.',
    iyzico_auth_failed: 'iyzico rejected the keys.',
    iyzico_unreachable: 'iyzico could not be reached. Try again later.',
    paytr_ok: 'PayTR connection verified.',
    paytr_credentials_missing: 'PayTR merchant ID, merchant key and salt are missing.',
    paytr_auth_failed: 'PayTR rejected the credentials.',
    paytr_unreachable: 'PayTR could not be reached. Try again later.',
    stripe_ok: 'Stripe connection verified (balance read).',
    stripe_credentials_missing: 'Stripe secret key and webhook signing secret are missing.',
    stripe_key_invalid:
      'The secret key must start with "sk_" (or restricted "rk_"), not the publishable "pk_" key.',
    stripe_not_test_key: 'The test environment needs a test key starting with "sk_test_".',
    stripe_test_key_in_live: 'A test key was entered for the live environment ("sk_live_" required).',
    stripe_webhook_secret_invalid:
      'The webhook signing secret must start with "whsec_" (Stripe → Webhooks → endpoint).',
    stripe_mode_mismatch: 'The key’s mode (test/live) does not match the selected environment.',
    stripe_auth_failed: 'Stripe did not accept the key.',
    stripe_unreachable: 'Stripe could not be reached. Try again later.',
    sipay_ok: 'Sipay connection verified (access token received).',
    sipay_credentials_missing: 'Sipay application ID, application secret and merchant key are missing.',
    sipay_auth_failed: 'Sipay credentials were not accepted.',
    sipay_unreachable: 'Sipay could not be reached. Try again later.',
    google_signin_client_ok: 'Google accepted the client ID and secret.',
    geliver_ok:
      'Geliver connection verified; {{senders}} sender address(es) found. Live: label fees are charged to the Geliver balance.',
    geliver_ok_test:
      'Geliver connection verified; {{senders}} sender address(es) found. Test environment: shipments are created in Geliver as test shipments.',
    geliver_credentials_missing: 'The Geliver API token is missing.',
    geliver_auth_failed:
      'Geliver rejected the API token. Check the token in the Geliver dashboard (API tokens).',
    geliver_timeout: 'Geliver did not respond within 30 seconds. Try again later.',
    geliver_unreachable: 'Geliver could not be reached. Try again later.',
    geliver_unavailable:
      'Geliver cannot respond right now (HTTP {{status}}). Try again in a few minutes; the label will not be bought twice.',
    geliver_rejected: 'Geliver rejected the request: {{reason}}',
    geliver_unexpected_response: 'Geliver returned an unexpected response; the operation was not completed.',
    paypal_ok: 'PayPal connection verified (webhook found).',
    paypal_credentials_missing: 'PayPal client ID, secret and webhook ID are missing.',
    paypal_auth_failed: 'PayPal did not accept the client ID or secret.',
    paypal_webhook_not_found: 'This webhook ID was not found in your PayPal app.',
    paypal_unreachable: 'PayPal could not be reached. Try again later.',
    bizimhesap_ok: 'BizimHesap connection verified.',
    bizimhesap_credentials_missing: 'BizimHesap firm ID (firmId) is missing.',
    bizimhesap_auth_failed: 'BizimHesap rejected the credentials. Check the firm ID/key.',
    bizimhesap_unreachable: 'BizimHesap could not be reached. Try again later.',
    bizimhesap_redirected:
      'BizimHesap redirected the request (HTTP {{status}} → {{location}}). The BizimHesap API address may have changed; the platform needs an update.',
    bizimhesap_unexpected_response:
      'BizimHesap returned an unexpected response (HTTP {{status}}, {{contentType}}); it is not JSON. The service may be under maintenance or the API address may have changed.',
    bizimhesap_timeout: 'BizimHesap did not respond within 20 seconds. Try again later.',
    netgsm_ok: 'Netgsm connection verified (balance read); no SMS sent.',
    netgsm_invalid_settings: 'Credentials and an SMS header of at most 11 characters are required.',
    netgsm_auth_failed: 'Netgsm rejected the credentials.',
    netgsm_unreachable: 'Could not reach Netgsm. Try again later.',
    netgsm_rejected: 'Netgsm rejected the request; check the account settings.',
    iletimerkezi_ok: 'İleti Merkezi connection verified (balance read); no SMS sent.',
    iletimerkezi_invalid_settings: 'Credentials and an SMS header of at most 11 characters are required.',
    iletimerkezi_auth_failed: 'İleti Merkezi rejected the credentials.',
    iletimerkezi_unreachable: 'Could not reach İleti Merkezi. Try again later.',
    iletimerkezi_rejected: 'İleti Merkezi rejected the request; check the account settings.',
    verimor_ok: 'Verimor connection verified (balance read); no SMS sent.',
    verimor_invalid_settings: 'Credentials and an SMS header of at most 11 characters are required.',
    verimor_auth_failed: 'Verimor rejected the credentials.',
    verimor_unreachable: 'Could not reach Verimor. Check the IP allowlist.',
    verimor_rejected: 'Verimor rejected the request; check the account settings.',
    whatsapp_ok: 'WhatsApp Business connection verified (number details read); no message sent.',
    whatsapp_invalid_settings:
      'Access token, phone number ID, WABA ID, app secret and verify token are required.',
    whatsapp_auth_failed: 'Meta rejected the access token or it lacks permission.',
    whatsapp_unreachable: 'Could not reach Meta (WhatsApp).',
    whatsapp_rejected: 'Meta rejected the request; check the phone number ID.',
    firebase_ok: 'Firebase service account verified (access token received); no notification sent.',
    firebase_invalid_settings:
      'Project ID, a service account email ending in …iam.gserviceaccount.com and the private key (PEM) are required.',
    firebase_auth_failed:
      'Google rejected the service account; check that the key was not deleted and the email is right.',
    firebase_unreachable: 'Could not reach Google. Try again later.',
    google_ads_invalid_settings: 'The manager account (MCC) ID must be 10 digits (e.g. 123-456-7890).',
    google_ads_settings_saved:
      "Saved. A developer token is no longer needed (Google, 9 September 2026); access depends on your Google Cloud project's level. The manager (MCC) ID is only needed if you access the account through a manager account.",
    google_ads_cloud_project_not_approved:
      'Your Google Cloud project can only access test accounts with the Google Ads API (Test access). For a real ad account, apply for Explorer access in Google Cloud: Google Cloud Console → Google Ads API → Overview (the project of the OAuth client used for the Google connection).',
    google_ads_user_permission_denied:
      'The connected Google account cannot access this Google Ads account. If you access it through a manager (MCC) account, enter the MCC ID under Integrations → Google Ads; if you did, check that it manages this account.',
    google_ads_customer_not_enabled: 'This Google Ads account is not enabled yet or has been closed.',
    google_ads_authorization_error:
      "Google Ads rejected the authorization. If the Cloud project's access level was upgraded after 9 September 2026, this may be a known issue reported by Google: create a new Google Cloud project and apply for Explorer access.",
    google_ads_api_not_enabled:
      'The Google Ads API is not enabled in the Google Cloud project or its terms of service were not accepted. Enable the Google Ads API in the Google Cloud Console.',
    google_scope_missing:
      'Google permission was not granted for this service. Reconnect with the service selected.',
    google_permission_denied:
      'The connected Google account has no access to this resource. Check that the resource (property, site, account) is shared with this Google account, or reconnect with an account that has access.',
    google_api_not_enabled:
      "This Google service's API is not enabled in the Google Cloud project. Enable it in Google Cloud Console → APIs & Services → Library (the project of the OAuth client used for the Google connection).",
    google_quota_exceeded:
      'Google request limit reached. Try again shortly; syncs are retried automatically.',
    google_ads_quota_exceeded:
      'Google Ads API request limit reached. Explorer access has a daily operation limit on real accounts; the operation is retried automatically later.',
    google_ads_access_prohibited:
      'Google Ads API access is blocked for this Google Cloud project. Check the Google Ads API access level in Google Cloud Console.',
    google_ads_action_not_permitted:
      'The connected Google account is not allowed to do this in this Google Ads account (it may have read-only access). Connect with Standard or Admin access.',
    google_ads_account_suspended:
      'The Google Ads account is suspended; no changes can be made. Check the account status in Google Ads.',
    google_ads_not_allowlisted:
      'This Google Ads feature is not available for your account (Google allowlist required).',
    google_ads_policy_violation:
      'Rejected under Google advertising policy. Edit the text or product; health products must not use claims like "treats", "prevents" or "cures". Details are in the campaign policy status.',
    google_ads_budget_too_low: 'The daily budget is too low for Google Ads. Enter a higher daily budget.',
    google_ads_budget_too_high: 'The daily budget exceeds the Google Ads maximum.',
    google_ads_budget_currency:
      "The budget amount does not match the account's currency or its smallest unit.",
    google_ads_budget_invalid:
      'Google Ads rejected the budget setting. Check that the budget is not used by another campaign and has not been removed.',
    google_ads_duplicate_name: 'A campaign or budget with this name already exists. Enter a different name.',
    google_ads_bid_out_of_range: 'The bid is outside Google Ads limits.',
    google_ads_bidding_invalid:
      'The selected bidding strategy cannot be used with this campaign type or budget.',
    google_ads_temporary_error:
      'Google Ads returned a temporary error. The operation is retried automatically shortly.',
    google_ads_resource_not_found:
      'The resource was not found in Google Ads (it may have been removed in Google Ads). Sync and try again.',
    google_ads_value_out_of_range: 'The value is outside Google Ads limits.',
    google_ads_resource_limit:
      'The Google Ads account has reached a resource limit (e.g. number of campaigns or assets).',
    google_ads_request_rejected:
      'Google Ads rejected the request. See the Google connection log for details.',
    ads_ok: 'Ad account read: {{account}} ({{currency}}). Spend data is synced every morning.',
    ads_credentials_format:
      'Access token and account ID are required; the account ID must contain digits only.',
    ads_token_invalid: 'The access token is invalid or expired. Create a new token and save it.',
    ads_permission: 'The token cannot read this ad account (ads_read / reporting permission).',
    ads_rate_limited: 'Platform rate limit reached; the sync will retry later.',
    ads_account_not_found: 'Ad account not found; check the ID.',
    ads_unreachable: 'Could not reach the ad platform. Try again later.',
    ads_timeout: 'The ad platform did not respond in time. Try again later.',
    ads_bad_response: 'Unexpected response from the ad platform.',
    ads_provider_error: 'The ad platform rejected the request. Check the platform error log for details.',
  },
  setupSteps: {
    business: 'Business details',
    brand: 'Add a brand',
    logo: 'Upload logo',
    colors: 'Pick colors',
    domain: 'Connect domain',
    currency: 'Currency and market',
    tax: 'Set up taxes',
    import_products: 'Import products',
    warehouse: 'Create warehouse',
    shipping: 'Connect shipping',
    pos_application: 'Prepare POS application',
    pos_connect: 'Connect POS',
    email_service: 'Connect email service',
    search_console: 'Connect Search Console',
    ga4: 'Connect GA4',
    gtm: 'Connect GTM',
    merchant_center: 'Connect Merchant Center',
    seo: 'Complete SEO settings',
    legal_pages: 'Review legal pages',
    test_order: 'Place a test order',
    payment_tests: 'Run payment tests',
    security_checks: 'Complete security checks',
    preview_store: 'Preview the store',
    go_live: 'Go live with approval',
  },
  setupStatus: { done: 'Done', pending: 'Pending', skipped: 'Skipped', locked: 'Locked' },
  storefront: storefrontEn,
  auditActions: {
    ...aiEn.auditActions,
    'company.member.add': 'Company user added',
    'company.member.update': "Company user's role or threshold changed",
    'company.member.remove': 'Company user removed',
    'customer.access_link.activation': 'Activation link created',
    'api_key.create': 'API key created',
    'api_key.revoke': 'API key revoked',
    'brand.create': 'Brand added',
    'brand.update': 'Brand updated',
    'domain.create': 'Domain added',
    'domain.delete': 'Domain removed',
    'help.update': 'Help text updated',
    'integration.credentials_saved': 'Integration credentials saved',
    'integration.disconnected': 'Integration disconnected',
    'integration.tested': 'Integration tested',
    'market.create': 'Market added',
    'market.delete': 'Market removed',
    'member.invitation_accepted': 'Invitation accepted',
    'member.invitation_revoke': 'Invitation revoked',
    'member.invite': 'User invited',
    'member.remove': 'User removed',
    'member.update': 'User role changed',
    'organization.update': 'Business details updated',
    'role.create': 'Custom role created',
    'role.delete': 'Custom role deleted',
    'role.update': 'Custom role updated',
    'settings.update': 'Settings updated',
    'setup.step_marked': 'Setup step marked',
    'store.create': 'Store created',
    'store.update': 'Store updated',
    'webhook.create': 'Webhook added',
    'webhook.delete': 'Webhook deleted',
    'webhook.redeliver': 'Webhook resent',
    'webhook.rotate_secret': 'Webhook signing secret rotated',
    'webhook.update': 'Webhook updated',
  },
  auditEntities: {
    company: 'Dealer company',
    customer: 'Customer',
    api_key: 'API key',
    brand: 'Brand',
    custom_role: 'Custom role',
    domain: 'Domain',
    help_entry: 'Help text',
    integration: 'Integration',
    invitation: 'Invitation',
    market: 'Market',
    membership: 'User',
    organization: 'Business',
    settings: 'Settings',
    setup_step: 'Setup step',
    store: 'Store',
    webhook_delivery: 'Webhook delivery',
    webhook_endpoint: 'Webhook',
  },
  healthClaims: {
    treatment: 'Treatment claim',
    cure: 'Cure claim',
    prevention: 'Prevention claim',
    disease: 'Disease name',
    drug: 'Drug wording',
    guarantee: 'Guaranteed result',
  },
  publishBlockers: {
    product_not_active: 'The product is not active.',
    vet_approval_required: 'Health content needs vet approval.',
    base_price_required: "No price in the store's base list.",
  },
  events: {
    'organization.updated': 'Business details changed',
    'brand.created': 'Brand created',
    'brand.updated': 'Brand updated',
    'store.created': 'Store created',
    'store.updated': 'Store updated',
    'member.invited': 'User invited',
    'member.updated': 'User role/status changed',
    'integration.error': 'Integration error',
    'integration.connected': 'Integration connected',
    'webhook.test': 'Test event',
    'product.created': 'Product created',
    'product.updated': 'Product updated',
    'product.published': 'Product published',
    'product.unpublished': 'Product unpublished',
    'price.changed': 'Price changed',
    'stock.changed': 'Stock changed',
    'customer.created': 'Customer created',
    'dealer.applied': 'Dealer application received',
    'dealer.approved': 'Dealer approved',
    'order.created': 'Order created',
    'order.status_changed': 'Order status changed',
    'order.updated': 'Order edited',
    'order.items_cancelled': 'Items cancelled in an order (partial cancellation)',
    'payment.captured': 'Payment captured',
    'payment.failed': 'Payment failed',
    'payment.review_required': 'Payment needs review (manual decision required)',
    'store.went_live': 'Store went live',
    'return.requested': 'Return/withdrawal request opened',
    'privacy.requested': 'Data subject request received',
    'privacy.anonymized': 'Erasure request: personal data anonymized',
    'form.submitted': 'Contact form message received',
    'dealer.balance_paid': 'Dealer paid their account balance by card',
    'return.completed': 'Return request completed',
    'theme.published': 'Theme published',
    'page.published': 'Page published',
    'post.published': 'Blog post published',
    'refund.created': 'Refund issued',
    'shipment.created': 'Shipment created',
    'shipment.updated': 'Shipment status changed',
    'invoice.sent': 'Invoice sent',
    'legal.published': 'Legal text published',
  },
} satisfies PartialMessages;
